/**
 * Booking Module Validation Schemas
 * 
 * Zod schemas for validating booking-related input data.
 * Used for form validation and API request validation.
 */

import { z } from 'zod';

// ============================================
// Booking Guest Schema
// ============================================

export const bookingGuestSchema = z.object({
  guestId: z.string().min(1, 'Guest ID is required'),
  personId: z.string().uuid().optional(),
  isOwner: z.boolean(),
});

export type BookingGuestInput = z.infer<typeof bookingGuestSchema>;

// ============================================
// Booking Form Data Schema
// ============================================

export const bookingFormDataSchema = z.object({
  locationId: z.string().uuid(),
  guests: z
    .array(bookingGuestSchema)
    .min(1, 'At least one guest is required')
    .refine(
      (guests) => guests.filter((g) => g.isOwner).length === 1,
      'Exactly one guest must be marked as owner'
    ),
  accommodationIds: z
    .array(z.string().min(1, 'Accommodation ID is required'))
    .min(1, 'At least one accommodation is required'),
  checkInDate: z.coerce.date({
    required_error: 'Check-in date is required',
    invalid_type_error: 'Invalid check-in date',
  }),
  checkOutDate: z.coerce.date({
    required_error: 'Check-out date is required',
    invalid_type_error: 'Invalid check-out date',
  }),
  notes: z
    .string()
    .max(1000, 'Notes must be less than 1000 characters')
    .optional(),
  seasonOverrideReason: z
    .string()
    .max(1000, 'Override reason must be less than 1000 characters')
    .optional(),
  totalAmount: z
    .number({ invalid_type_error: 'Total amount must be a number' })
    .min(0, 'Total amount cannot be negative'),
  referenceId: z
    .string()
    .max(100, 'Reference ID must be less than 100 characters')
    .optional(),
  bookingType: z
    .enum(['guest', 'staff_guest', 'day_use'])
    .default('guest'),
  sponsoredBy: z
    .string()
    .uuid('Invalid staff ID')
    .optional()
    .nullable(),
  dailyRate: z
    .number({ invalid_type_error: 'Daily rate must be a number' })
    .min(0, 'Daily rate cannot be negative')
    .optional()
    .nullable(),
}).refine(
  (data) => data.checkOutDate > data.checkInDate,
  {
    message: 'Check-out date must be after check-in date',
    path: ['checkOutDate'],
  }
);

export type BookingFormDataInput = z.infer<typeof bookingFormDataSchema>;

// ============================================
// Cancellation Request Schema
// ============================================

export const cancellationRequestSchema = z.object({
  bookingId: z.string().min(1, 'Booking ID is required'),
  reason: z
    .string()
    .min(10, 'Reason must be at least 10 characters')
    .max(500, 'Reason must be less than 500 characters'),
  calculateFee: z.boolean().default(true),
  refundAmount: z
    .number()
    .min(0, 'Refund amount cannot be negative')
    .optional(),
});

export type CancellationRequestInput = z.infer<typeof cancellationRequestSchema>;

// ============================================
// Payment Record Schema
// ============================================

export const paymentMethodSchema = z.enum(['cash', 'card', 'transfer', 'external'], {
  errorMap: () => ({ message: 'Invalid payment method' }),
});

export const bookingPaymentSchema = z.object({
  bookingId: z.string().min(1, 'Booking ID is required'),
  amount: z
    .number({ invalid_type_error: 'Amount must be a number' })
    .positive('Amount must be greater than zero'),
  method: paymentMethodSchema,
  reference: z
    .string()
    .max(100, 'Reference must be less than 100 characters')
    .optional(),
  notes: z
    .string()
    .max(500, 'Notes must be less than 500 characters')
    .optional(),
});

export type BookingPaymentInput = z.infer<typeof bookingPaymentSchema>;

// ============================================
// Booking Filters Schema
// ============================================

export const bookingStatusSchema = z.enum([
  'pending',
  'confirmed',
  'checked-in',
  'checked-out',
  'cancelled',
  'no-show',
]);

export const paymentStatusSchema = z.enum([
  'unpaid',
  'partially-paid',
  'paid',
  'refunded',
]);

export const bookingFiltersSchema = z.object({
  status: z.array(bookingStatusSchema).optional(),
  paymentStatus: z.array(paymentStatusSchema).optional(),
  guestId: z.string().optional(),
  accommodationId: z.string().optional(),
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
  externalSource: z.string().optional(),
  search: z
    .string()
    .max(100, 'Search query must be less than 100 characters')
    .optional(),
}).refine(
  (data) => {
    if (data.dateFrom && data.dateTo) {
      return data.dateTo >= data.dateFrom;
    }
    return true;
  },
  {
    message: 'End date must be on or after start date',
    path: ['dateTo'],
  }
);

export type BookingFiltersInput = z.infer<typeof bookingFiltersSchema>;

// ============================================
// Status Change Schema
// ============================================

export const statusChangeSchema = z.object({
  id: z.string().min(1, 'Booking ID is required'),
  status: bookingStatusSchema,
});

export type StatusChangeInput = z.infer<typeof statusChangeSchema>;

// ============================================
// Validation Helper Functions
// ============================================

/**
 * Validates booking form data and returns parsed result or throws
 */
export const validateBookingFormData = (data: unknown): BookingFormDataInput => {
  return bookingFormDataSchema.parse(data);
};

/**
 * Validates booking form data and returns safe result
 */
export const safeValidateBookingFormData = (data: unknown) => {
  return bookingFormDataSchema.safeParse(data);
};

/**
 * Validates cancellation request
 */
export const validateCancellationRequest = (data: unknown): CancellationRequestInput => {
  return cancellationRequestSchema.parse(data);
};

/**
 * Validates payment record
 */
export const validateBookingPayment = (data: unknown): BookingPaymentInput => {
  return bookingPaymentSchema.parse(data);
};

/**
 * Validates booking filters
 */
export const validateBookingFilters = (data: unknown): BookingFiltersInput => {
  return bookingFiltersSchema.parse(data);
};
